<template>
  <v-card class="weather-widget" elevation="0">
    <div class="weather-container">
      
      <!-- Sección izquierda: Clima actual -->
      <div class="weather-current" :style="weatherBgStyle">
        <div class="weather-overlay pa-4">

          <!-- Header -->
          <div class="d-flex align-center justify-space-between mb-2">
            <div class="d-flex align-center" style="gap: 6px;">
              <v-icon small color="white" class="mr-1">mdi-map-marker-outline</v-icon>
              <span class="white--text caption font-weight-medium location-text" v-if="!loadingWeather">
                {{ locationLabel }}
              </span>
              <v-skeleton-loader v-else type="text" width="100" dark />
            </div>
            <v-btn icon x-small @click="fetchWeather" :loading="loadingWeather">
              <v-icon small color="white">mdi-refresh</v-icon>
            </v-btn>
          </div>

          <!-- Main temp -->
          <div v-if="!loadingWeather && weather" class="weather-main">
            <div class="d-flex align-center" style="gap: 8px;">
              <v-icon :size="56" :color="weather.iconColor" style="filter: drop-shadow(0 2px 6px rgba(0,0,0,0.3));">
                {{ weather.icon }}
              </v-icon>
              <div>
                <div class="d-flex align-end">
                  <span class="weather-temp white--text">{{ weather.temp }}</span>
                  <span class="white--text ml-1 mb-2" style="font-size:16px; opacity:0.85;">°{{ weather.unit }}</span>
                </div>
                <p class="white--text mb-0 weather-desc">{{ weather.description }}</p>
              </div>
            </div>
          </div>

          <!-- Skeleton while loading -->
          <div v-else-if="loadingWeather" class="weather-main">
            <v-skeleton-loader type="heading" dark width="120" />
            <v-skeleton-loader type="text" dark width="80" class="mt-1" />
          </div>

          <!-- Error -->
          <div v-else-if="error" class="d-flex align-center" style="gap:6px; margin-top:8px;">
            <v-icon small color="white">mdi-alert-circle-outline</v-icon>
            <span class="white--text caption">No se pudo obtener el clima</span>
          </div>

          <!-- Details row -->
          <div v-if="!loadingWeather && weather" class="weather-details mt-3">
            <div class="weather-detail-item">
              <v-icon x-small color="rgba(255,255,255,0.75)">mdi-water-percent</v-icon>
              <div class="ml-1">
                <div class="white--text caption font-weight-bold">{{ weather.humidity }}%</div>
                <div class="white--text" style="font-size: 9px; opacity:0.6;">Humedad</div>
              </div>
            </div>
            <div class="weather-detail-item">
              <v-icon x-small color="rgba(255,255,255,0.75)">mdi-weather-windy</v-icon>
              <div class="ml-1">
                <div class="white--text caption font-weight-bold">{{ weather.windSpeed }} km/h</div>
                <div class="white--text" style="font-size: 9px; opacity:0.6;">Viento</div>
              </div>
            </div>
            <div class="weather-detail-item" v-if="weather.feelsLike !== null">
              <v-icon x-small color="rgba(255,255,255,0.75)">mdi-thermometer</v-icon>
              <div class="ml-1">
                <div class="white--text caption font-weight-bold">{{ weather.feelsLike }}°</div>
                <div class="white--text" style="font-size: 9px; opacity:0.6;">Sensación</div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <!-- Sección derecha: Pronóstico -->
      <div class="weather-forecast" v-if="!loadingWeather && weather && weather.forecast">
        <div class="forecast-header pa-2">
          <v-icon x-small color="#9E9E9E" class="mr-1">mdi-calendar-range</v-icon>
          <span class="text--secondary caption font-weight-bold">Próximo 14 días</span>
        </div>
        
        <!-- Grid de tarjetas -->
        <div class="forecast-grid pa-2">
          <div
            v-for="(day, i) in weather.forecast"
            :key="i"
            class="forecast-card"
            :class="{ 'forecast-card--selected': selectedDayIndex === i }"
            @click="toggleDay(i)"
          >
            <div class="forecast-card-main">
              <div class="forecast-card-day">{{ day.day }}</div>
              <div class="forecast-card-date">{{ day.dateFormatted }}</div>
              <v-icon :size="32" :color="day.iconColor">{{ day.icon }}</v-icon>
              <div class="forecast-card-temps">
                <span class="temp-max">{{ day.max }}°</span>
                <span class="temp-separator">/</span>
                <span class="temp-min">{{ day.min }}°</span>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Pronóstico por hora expandido -->
        <v-expand-transition>
          <div v-if="selectedDayIndex !== null && weather.forecast[selectedDayIndex]" class="hours-expanded">
            <div class="hours-header pa-2">
              <v-icon x-small color="#616161" class="mr-1">mdi-clock-outline</v-icon>
              <span class="text--secondary caption font-weight-bold">Pronóstico por hora</span>
              <v-spacer></v-spacer>
              <v-btn icon x-small @click.stop="selectedDayIndex = null">
                <v-icon small color="#9E9E9E">mdi-close</v-icon>
              </v-btn>
            </div>
            <div class="hours-list pa-2">
              <div
                v-for="(hour, h) in weather.forecast[selectedDayIndex].hours"
                :key="h"
                class="hour-item"
              >
                <span class="hour-time">{{ formatHour(hour.time) }}</span>
                <v-icon small :color="getIconByCode(hour.condition.code, hour.is_day === 0).color">
                  {{ getIconByCode(hour.condition.code, hour.is_day === 0).icon }}
                </v-icon>
                <span class="hour-temp">{{ Math.round(hour.temp_c) }}°</span>
                <div class="hour-details">
                  <v-icon x-small color="#90A4AE">mdi-water-percent</v-icon>
                  <span class="caption text--secondary">{{ hour.humidity }}%</span>
                </div>
                <span class="hour-condition caption text--secondary">{{ hour.condition.text }}</span>
              </div>
            </div>
          </div>
        </v-expand-transition>
      </div>
      
    </div>
  </v-card>
</template>

<script>
import { WEATHER_ICONS_BY_CODE, DAY_LABELS } from '@/helpers/constants/weather'

export default {
  name: 'WeatherWidget',
  props: {
    // Override location from parent if needed
    lat: { type: Number, default: null },
    lon: { type: Number, default: null },
    locationName: { type: String, default: null }
  },
  data: () => ({
    weather: null,
    loadingWeather: false,
    error: false,
    detectedLocation: null,
    selectedDayIndex: null
  }),
  computed: {
    selectedProject() {
      return this.$store.projectsStore.state.selectedProject
    },
    locationLabel() {
      return this.locationName || this.detectedLocation || 'Mi ubicación'
    },
    weatherBgStyle() {
      if (!this.weather) return { background: 'linear-gradient(135deg, #546E7A, #37474F)' }
      const bg = this.weather.bg || ['#546E7A', '#37474F']
      return { background: `linear-gradient(135deg, ${bg[0]}, ${bg[1]})` }
    }
  },
  mounted() {
    this.fetchWeather()
  },
  methods: {
    async fetchWeather() {
      this.loadingWeather = true
      this.error = false
      try {
				// const { lat, lon } = await this.getCoords()
				let lat = this.selectedProject?.geo?.latitude
				let lon = this.selectedProject?.geo?.longitude
				if(!lat || !lon) {
					({ lat, lon } = await this.getCoords())
				}
        const apiKey = process.env.VUE_APP_OPENWEATHER_API_KEY || 'ea3a7b1921494a81a9e52003261404'
				if(!lat || !lon || !apiKey) {
					return null
				}
        const url = `https://api.weatherapi.com/v1/forecast.json?key=${apiKey}&q=${lat},${lon}&days=14&lang=es`
        const res = await fetch(url)
        const data = await res.json()
        this.weather = this.parseWeatherData(data)
        if (!this.locationName) {
          this.detectedLocation = data.location?.name || 'Mi ubicación'
        }
      } catch (e) {
        console.error('Weather error:', e)
        this.error = true
        this.weather = null
      } finally {
        this.loadingWeather = false
      }
    },

    getCoords() {
      if (this.lat !== null && this.lon !== null) {
        return Promise.resolve({ lat: this.lat, lon: this.lon })
      }
      return new Promise((resolve, reject) => {
        if (!navigator.geolocation) return reject(new Error('Geolocation not supported'))
        navigator.geolocation.getCurrentPosition(
          pos => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
          err => reject(err),
          { timeout: 8000 }
        )
      })
    },

    parseWeatherData (data) {
      const current  = data.current
      const code     = current.condition.code
      const text     = current.condition.text
      const isNight  = current.is_day === 0
      const meta     = this.getIconByCode(code, isNight)

      const forecast = data.forecast.forecastday.slice(0, 14).map(day => {
        const date = new Date(day.date + 'T12:00:00')
        const dm   = this.getIconByCode(day.day.condition.code, false)
        const dtext = day.day.condition.text
        const [, m, d] = day.date.split('-')
        return {
          day:           DAY_LABELS[date.getDay()],
          dateFormatted: `${d}/${m}`,
          max:           Math.round(day.day.maxtemp_c),
          min:           Math.round(day.day.mintemp_c),
          text:          dtext,
          icon:          dm.icon,
          iconColor:     dm.color,
          hours:         day.hour || []
        }
      })

      return {
        temp:        Math.round(current.temp_c),
        feelsLike:   Math.round(current.feelslike_c),
        humidity:    current.humidity,
        windSpeed:   Math.round(current.wind_kph),
        description: this.capitalize(current.condition.text),
        unit:        'C',
        icon:        meta.icon,
        iconColor:   meta.color,
        bg:          meta.bg,
				text,
        forecast
      }
    },

    getIconByCode(code, isNight) {
      if (isNight && code === 1000) {
        return { icon: 'mdi-weather-night', color: '#90CAF9', bg: ['#1A237E', '#0D47A1'] }
      }
      return WEATHER_ICONS_BY_CODE[code] || WEATHER_ICONS_BY_CODE.default
    },

    getDemoData() {
      return {
        temp: 22,
        feelsLike: 21,
        humidity: 58,
        windSpeed: 14,
        description: 'Parcialmente nublado',
        unit: 'C',
        icon: 'mdi-weather-partly-cloudy',
        iconColor: '#90A4AE',
        bg: ['#42A5F5', '#1976D2'],
        forecast: [
          { day: 'Lun', max: 24, min: 16, icon: 'mdi-weather-sunny', iconColor: '#FFC107' },
          { day: 'Mar', max: 21, min: 14, icon: 'mdi-weather-rainy', iconColor: '#64B5F6' },
          { day: 'Mié', max: 19, min: 13, icon: 'mdi-weather-cloudy', iconColor: '#90A4AE' },
          { day: 'Jue', max: 23, min: 15, icon: 'mdi-weather-sunny', iconColor: '#FFC107' },
          { day: 'Vie', max: 25, min: 17, icon: 'mdi-weather-partly-cloudy', iconColor: '#90A4AE' }
        ]
      }
    },

    capitalize(str) {
      return str ? str.charAt(0).toUpperCase() + str.slice(1) : ''
    },

    toggleDay(index) {
      this.selectedDayIndex = this.selectedDayIndex === index ? null : index
    },

    formatHour(timeStr) {
      return timeStr ? timeStr.split(' ')[1] : ''
    }
  }
}
</script>

<style scoped>
.weather-widget {
  border: 1px solid #E0E0E0;
  border-radius: 12px !important;
  overflow: hidden;
  height: 100%;
}

.weather-container {
  display: flex;
  height: 100%;
  min-height: 200px;
}

/* Sección izquierda: Clima actual */
.weather-current {
  flex: 0 0 280px;
  transition: background 0.6s ease;
  display: flex;
  flex-direction: column;
}

.weather-overlay {
  background: linear-gradient(0deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.05) 100%);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.weather-temp {
  font-size: 48px;
  font-weight: 300;
  line-height: 1;
  letter-spacing: -2px;
}

.weather-desc {
  font-size: 12px;
  opacity: 0.9;
  text-transform: capitalize;
}

.location-text {
  font-size: 12px;
  opacity: 0.9;
}

.weather-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.weather-detail-item {
  display: flex;
  align-items: center;
}

/* Sección derecha: Pronóstico */
.weather-forecast {
  flex: 1;
  background: #FAFAFA;
  display: flex;
  flex-direction: column;
  border-left: 1px solid rgba(0,0,0,0.08);
  overflow: hidden;
}

.forecast-header {
  display: flex;
  align-items: center;
  border-bottom: 1px solid rgba(0,0,0,0.06);
  background: #F5F5F5;
  flex-shrink: 0;
}

/* Grid de tarjetas cuadradas */
.forecast-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(105px, 1fr));
  gap: 10px;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 2px;
  min-height: 0;
}

.forecast-card {
  background: white;
  border-radius: 12px;
  border: 2px solid rgba(0,0,0,0.06);
  cursor: pointer;
  transition: all 0.2s ease;
  overflow: visible;
  min-height: 135px;
  position: relative;
}

.forecast-card:hover {
  border-color: rgba(46, 125, 50, 0.3);
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  transform: translateY(-2px);
  z-index: 1;
}

.forecast-card--selected {
  border-color: #2E7D32;
  background: #E8F5E9;
  box-shadow: 0 2px 12px rgba(46, 125, 50, 0.2);
  z-index: 2;
}

.forecast-card-main {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-evenly;
  padding: 10px 6px;
  gap: 2px;
  height: 100%;
  min-height: 135px;
}

.forecast-card-day {
  font-size: 12px;
  font-weight: 700;
  color: #424242;
  text-align: center;
  line-height: 1.1;
  width: 100%;
}

.forecast-card-date {
  font-size: 10px;
  color: #9E9E9E;
  text-align: center;
  margin-bottom: 4px;
  width: 100%;
}

.forecast-card-main .v-icon {
  margin: 6px 0;
  flex-shrink: 0;
}

.forecast-card-temps {
  display: flex;
  align-items: baseline;
  gap: 4px;
  justify-content: center;
  margin-top: 4px;
  width: 100%;
}

.forecast-card-temps .temp-max {
  font-size: 14px;
  font-weight: 700;
  color: #E65100;
  line-height: 1;
}

.forecast-card-temps .temp-separator {
  font-size: 11px;
  color: #BDBDBD;
  margin: 0 -1px;
}

.forecast-card-temps .temp-min {
  font-size: 13px;
  color: #546E7A;
  font-weight: 500;
  line-height: 1;
}

/* Panel de horas expandido */
.hours-expanded {
  border-top: 2px solid #2E7D32;
  background: #FAFAFA;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.hours-header {
  display: flex;
  align-items: center;
  background: #F5F5F5;
  border-bottom: 1px solid rgba(0,0,0,0.06);
  flex-shrink: 0;
}

.hours-list {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-height: 0;
  padding-bottom: 8px;
}

.hour-item {
  display: grid;
  grid-template-columns: 50px 40px 50px 60px 1fr;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background: white;
  border-radius: 6px;
  transition: background 0.2s;
}

.hour-item:hover {
  background: #F5F5F5;
}

.hour-time {
  font-size: 12px;
  font-weight: 600;
  color: #424242;
}

.hour-temp {
  font-size: 13px;
  font-weight: 700;
  color: #E65100;
}

.hour-details {
  display: flex;
  align-items: center;
  gap: 4px;
}

.hour-condition {
  font-size: 11px;
  color: #757575;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Remover estilos de lista obsoletos */
.forecast-list,
.forecast-row,
.forecast-day,
.forecast-temps {
  display: none;
}

/* Responsive: stack verticalmente en pantallas pequeñas */
@media (max-width: 800px) {
  .weather-container {
    flex-direction: column;
  }
  
  .weather-current {
    flex: 0 0 auto;
  }
  
  .weather-forecast {
    border-left: none;
    border-top: 1px solid rgba(0,0,0,0.08);
  }
}
</style>
<style>
.wxv-tooltip {
  background: rgba(25, 35, 55, 0.94) !important;
  border-radius: 10px !important;
  padding: 7px 12px !important;
  font-size: 12px;
  letter-spacing: 0.2px;
  box-shadow: 0 4px 14px rgba(0,0,0,0.28);
}
.wxv-tooltip-text {
  color: rgba(255,255,255,0.9);
  font-weight: 400;
}
</style>